/**
 * AdminDashboard Page
 * 관리자 대시보드 - 통계 요약
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { StatCard } from '../../components/molecules/StatCard';
import { adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/atoms/Button/Button';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 정산비율 상태
  const [referralRates, setReferralRates] = useState<{ regular: number; special: number } | null>(null);
  const [regularRate, setRegularRate] = useState<string>('50');
  const [specialRate, setSpecialRate] = useState<string>('70');
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesMessage, setRatesMessage] = useState<string | null>(null);

  // 가격 설정 상태
  const [pricing, setPricing] = useState<any>(null);
  const [servicePrices, setServicePrices] = useState({
    today: '3900',
    saju: '9900',
    tojung: '7900',
    compatibility: '12900',
    wealth: '5900',
    love: '6900'
  });
  const [subscriptionPrices, setSubscriptionPrices] = useState({
    '1day': '9900',
    '7days': '39000',
    '30days': '99000'
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingMessage, setPricingMessage] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
    loadReferralRates();
    loadPricing();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response: any = await adminAPI.getAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError('통계 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadReferralRates = async () => {
    try {
      const response: any = await adminAPI.getReferralRates();
      if (response.success) {
        const rates = response.data;
        setReferralRates(rates);
        setRegularRate((rates.regular * 100).toString());
        setSpecialRate((rates.special * 100).toString());
      }
    } catch (err: any) {
      console.error('Failed to load referral rates:', err);
      // 기본값 유지
    }
  };

  const handleUpdateRates = async () => {
    try {
      setRatesLoading(true);
      setRatesMessage(null);

      // 입력값 검증 (0-100 범위)
      const regularPercent = parseFloat(regularRate);
      const specialPercent = parseFloat(specialRate);

      if (isNaN(regularPercent) || isNaN(specialPercent)) {
        setRatesMessage('비율은 숫자여야 합니다.');
        return;
      }

      if (regularPercent < 0 || regularPercent > 100 || specialPercent < 0 || specialPercent > 100) {
        setRatesMessage('비율은 0과 100 사이의 값이어야 합니다.');
        return;
      }

      // 백엔드로 전송 시 0-1 범위로 변환
      const regular = regularPercent / 100;
      const special = specialPercent / 100;

      const response: any = await adminAPI.updateReferralRates(regular, special);
      if (response.success) {
        setRatesMessage('정산비율이 성공적으로 업데이트되었습니다.');
        await loadReferralRates();
      }
    } catch (err: any) {
      console.error('Failed to update rates:', err);
      setRatesMessage('정산비율 업데이트에 실패했습니다.');
    } finally {
      setRatesLoading(false);
    }
  };

  const handleInitConfig = async () => {
    try {
      setRatesLoading(true);
      setRatesMessage(null);

      const response: any = await adminAPI.initSystemConfig();
      if (response.success) {
        setRatesMessage('시스템 설정이 초기화되었습니다.');
        await loadReferralRates();
      } else {
        setRatesMessage(response.message || '이미 설정이 존재합니다.');
      }
    } catch (err: any) {
      console.error('Failed to init config:', err);
      setRatesMessage('설정 초기화에 실패했습니다.');
    } finally {
      setRatesLoading(false);
    }
  };

  const loadPricing = async () => {
    try {
      const response: any = await adminAPI.getPricing();
      if (response.success) {
        const data = response.data;
        setPricing(data);

        // 서비스 가격 로드
        if (data.servicePrices) {
          setServicePrices({
            today: data.servicePrices.today?.toString() || '3900',
            saju: data.servicePrices.saju?.toString() || '9900',
            tojung: data.servicePrices.tojung?.toString() || '7900',
            compatibility: data.servicePrices.compatibility?.toString() || '12900',
            wealth: data.servicePrices.wealth?.toString() || '5900',
            love: data.servicePrices.love?.toString() || '6900'
          });
        }

        // 구독 가격 로드
        if (data.subscriptionPrices) {
          setSubscriptionPrices({
            '1day': data.subscriptionPrices['1day']?.toString() || '9900',
            '7days': data.subscriptionPrices['7days']?.toString() || '39000',
            '30days': data.subscriptionPrices['30days']?.toString() || '99000'
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to load pricing:', err);
      // 기본값 유지
    }
  };

  const handleUpdatePricing = async () => {
    try {
      setPricingLoading(true);
      setPricingMessage(null);

      // 입력값 검증
      const servicePricesObj: Record<string, number> = {};
      for (const [key, value] of Object.entries(servicePrices)) {
        const price = parseInt(value);
        if (isNaN(price)) {
          setPricingMessage(`${key} 가격은 숫자여야 합니다.`);
          return;
        }
        if (price < 1000 || price > 1000000) {
          setPricingMessage(`가격은 1,000원 ~ 1,000,000원 사이여야 합니다. (${key}: ${price}원)`);
          return;
        }
        servicePricesObj[key] = price;
      }

      const subscriptionPricesObj: Record<string, number> = {};
      for (const [key, value] of Object.entries(subscriptionPrices)) {
        const price = parseInt(value);
        if (isNaN(price)) {
          setPricingMessage(`${key} 가격은 숫자여야 합니다.`);
          return;
        }
        if (price < 1000 || price > 1000000) {
          setPricingMessage(`가격은 1,000원 ~ 1,000,000원 사이여야 합니다. (${key}: ${price}원)`);
          return;
        }
        subscriptionPricesObj[key] = price;
      }

      const response: any = await adminAPI.updatePricing(servicePricesObj, subscriptionPricesObj);
      if (response.success) {
        // 가격 재로드
        await loadPricing();

        // 성공 메시지에 실제 저장된 값 표시
        setPricingMessage('✅ 가격이 성공적으로 업데이트되었습니다. 페이지를 새로고침하여 확인해주세요.');

        // 3초 후 페이지 새로고침하여 Firestore 값 재확인
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Failed to update pricing:', err);
      setPricingMessage('가격 업데이트에 실패했습니다.');
    } finally {
      setPricingLoading(false);
    }
  };

  const handleInitPricing = async () => {
    try {
      setPricingLoading(true);
      setPricingMessage(null);

      const response: any = await adminAPI.initPricingConfig();
      if (response.success) {
        setPricingMessage('가격 설정이 초기화되었습니다.');
        await loadPricing();
      } else {
        setPricingMessage(response.message || '이미 가격 설정이 존재합니다.');
      }
    } catch (err: any) {
      console.error('Failed to init pricing:', err);
      setPricingMessage('가격 설정 초기화에 실패했습니다.');
    } finally {
      setPricingLoading(false);
    }
  };

  return (
    <>
      <Header>
          <HeaderContent>
            <Title>관리자 대시보드</Title>
            <Subtitle>LuckyOn 서비스 통계 요약</Subtitle>
          </HeaderContent>

          <Actions>
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/admin/users')}
            >
              사용자 관리
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/admin/payments')}
            >
              결제 관리
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/admin/withdrawals')}
            >
              출금 관리
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/admin/referral-history')}
            >
              레퍼럴 발생이력
            </Button>
          </Actions>
        </Header>

        {loading && (
          <LoadingMessage>데이터를 불러오는 중...</LoadingMessage>
        )}

        {error && (
          <ErrorMessage>{error}</ErrorMessage>
        )}

        {!loading && !error && analytics && (
          <Content>
            <Section>
              <SectionTitle>사용자 통계</SectionTitle>
              <StatsGrid>
                <StatCard
                  title="전체 사용자"
                  value={analytics.totalUsers}
                  icon="👥"
                  unit="명"
                />
                <StatCard
                  title="일반회원"
                  value={analytics.usersByGrade?.member || 0}
                  icon="👤"
                  unit="명"
                />
                <StatCard
                  title="정회원"
                  value={analytics.usersByGrade?.regular || 0}
                  icon="⭐"
                  unit="명"
                />
                <StatCard
                  title="특별회원"
                  value={analytics.usersByGrade?.special || 0}
                  icon="💎"
                  unit="명"
                />
              </StatsGrid>
            </Section>

            <Section>
              <SectionTitle>결제 통계</SectionTitle>
              <StatsGrid>
                <StatCard
                  title="총 결제 건수"
                  value={analytics.totalPayments}
                  icon="💳"
                  unit="건"
                />
                <StatCard
                  title="총 매출"
                  value={analytics.totalRevenue}
                  icon="💰"
                  unit="원"
                />
                <StatCard
                  title="활성 구독"
                  value={analytics.activeSubscriptions}
                  icon="📅"
                  unit="건"
                />
                <StatCard
                  title="대기중 출금"
                  value={analytics.pendingWithdrawals}
                  icon="🏦"
                  unit="원"
                />
              </StatsGrid>
            </Section>

            <Section>
              <SectionTitle>운세 서비스 통계</SectionTitle>
              <StatsGrid>
                <StatCard
                  title="총 생성 수"
                  value={analytics.totalFortuneGenerated}
                  icon="🔮"
                  unit="건"
                />
                <StatCard
                  title="오늘의 운세"
                  value={analytics.fortuneByType?.today || 0}
                  unit="건"
                />
                <StatCard
                  title="사주팔자"
                  value={analytics.fortuneByType?.saju || 0}
                  unit="건"
                />
                <StatCard
                  title="토정비결"
                  value={analytics.fortuneByType?.tojung || 0}
                  unit="건"
                />
                <StatCard
                  title="궁합"
                  value={analytics.fortuneByType?.compatibility || 0}
                  unit="건"
                />
                <StatCard
                  title="재물운"
                  value={analytics.fortuneByType?.wealth || 0}
                  unit="건"
                />
                <StatCard
                  title="연애운"
                  value={analytics.fortuneByType?.love || 0}
                  unit="건"
                />
              </StatsGrid>
            </Section>

            <Section>
              <SectionTitle>리퍼럴 정산비율 설정</SectionTitle>
              <SettingsCard>
                <SettingsRow>
                  <SettingsLabel>
                    <SettingsLabelText>정회원 정산비율 ⭐</SettingsLabelText>
                    <SettingsDescription>정회원이 추천한 사용자의 결제 시 적용</SettingsDescription>
                  </SettingsLabel>
                  <InputGroup>
                    <PercentInput
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={regularRate}
                      onChange={(e) => setRegularRate(e.target.value)}
                      disabled={ratesLoading}
                    />
                    <PercentSymbol>%</PercentSymbol>
                  </InputGroup>
                </SettingsRow>

                <SettingsRow>
                  <SettingsLabel>
                    <SettingsLabelText>특별회원 정산비율 💎</SettingsLabelText>
                    <SettingsDescription>특별회원이 추천한 사용자의 결제 시 적용</SettingsDescription>
                  </SettingsLabel>
                  <InputGroup>
                    <PercentInput
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={specialRate}
                      onChange={(e) => setSpecialRate(e.target.value)}
                      disabled={ratesLoading}
                    />
                    <PercentSymbol>%</PercentSymbol>
                  </InputGroup>
                </SettingsRow>

                <SettingsActions>
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleUpdateRates}
                    disabled={ratesLoading}
                  >
                    {ratesLoading ? '저장 중...' : '비율 저장'}
                  </Button>
                  {!referralRates && (
                    <Button
                      variant="secondary"
                      size="medium"
                      onClick={handleInitConfig}
                      disabled={ratesLoading}
                    >
                      설정 초기화
                    </Button>
                  )}
                </SettingsActions>

                {ratesMessage && (
                  <SettingsMessage $isError={ratesMessage.includes('실패')}>
                    {ratesMessage}
                  </SettingsMessage>
                )}

                <SettingsNote>
                  💡 정산비율 변경은 소급 적용되지 않으며, 변경 이후 발생하는 결제부터 새로운 비율이 적용됩니다.
                </SettingsNote>
              </SettingsCard>
            </Section>

            <Section>
              <SectionTitle>가격 관리</SectionTitle>
              <SettingsCard>
                <SectionSubtitle>일회성 서비스 가격 💫</SectionSubtitle>

                <PricingGrid>
                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>오늘의 운세</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={servicePrices.today}
                        onChange={(e) => setServicePrices({ ...servicePrices, today: e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>사주팔자</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={servicePrices.saju}
                        onChange={(e) => setServicePrices({ ...servicePrices, saju: e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>토정비결</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={servicePrices.tojung}
                        onChange={(e) => setServicePrices({ ...servicePrices, tojung: e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>궁합</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={servicePrices.compatibility}
                        onChange={(e) => setServicePrices({ ...servicePrices, compatibility: e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>재물운</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={servicePrices.wealth}
                        onChange={(e) => setServicePrices({ ...servicePrices, wealth: e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>연애운</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={servicePrices.love}
                        onChange={(e) => setServicePrices({ ...servicePrices, love: e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>
                </PricingGrid>

                <Divider />

                <SectionSubtitle>구독 상품 가격 📅</SectionSubtitle>

                <PricingGrid>
                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>1일 이용권</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={subscriptionPrices['1day']}
                        onChange={(e) => setSubscriptionPrices({ ...subscriptionPrices, '1day': e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>7일 이용권</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={subscriptionPrices['7days']}
                        onChange={(e) => setSubscriptionPrices({ ...subscriptionPrices, '7days': e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>

                  <PricingRow>
                    <SettingsLabel>
                      <SettingsLabelText>30일 이용권</SettingsLabelText>
                    </SettingsLabel>
                    <InputGroup>
                      <PriceInput
                        type="number"
                        min="1000"
                        max="1000000"
                        step="100"
                        value={subscriptionPrices['30days']}
                        onChange={(e) => setSubscriptionPrices({ ...subscriptionPrices, '30days': e.target.value })}
                        disabled={pricingLoading}
                      />
                      <PercentSymbol>원</PercentSymbol>
                    </InputGroup>
                  </PricingRow>
                </PricingGrid>

                <SettingsActions>
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleUpdatePricing}
                    disabled={pricingLoading}
                  >
                    {pricingLoading ? '저장 중...' : '가격 저장'}
                  </Button>
                  {!pricing && (
                    <Button
                      variant="secondary"
                      size="medium"
                      onClick={handleInitPricing}
                      disabled={pricingLoading}
                    >
                      설정 초기화
                    </Button>
                  )}
                </SettingsActions>

                {pricingMessage && (
                  <SettingsMessage $isError={pricingMessage.includes('실패')}>
                    {pricingMessage}
                  </SettingsMessage>
                )}

                <SettingsNote>
                  💡 가격 변경은 소급 적용되지 않으며, 변경 이후 발생하는 결제부터 새로운 가격이 적용됩니다.
                </SettingsNote>
              </SettingsCard>
            </Section>

            <Section>
              <SectionTitle>리퍼럴 통계</SectionTitle>
              <StatsGrid>
                <StatCard
                  title="총 리퍼럴 크레딧"
                  value={analytics.totalReferralCredits}
                  icon="🎁"
                  unit="원"
                />
              </StatsGrid>
            </Section>
          </Content>
        )}
    </>
  );
};

const Header = styled.div`
  padding: ${tokens.spacing[8]} ${tokens.spacing[6]} ${tokens.spacing[6]};
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${tokens.spacing[4]};

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin: 0 0 ${tokens.spacing[2]} 0;
`;

const Subtitle = styled.p`
  font-size: ${tokens.typography.fontSize.base};
  color: ${tokens.colors.neutral[600]};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};

  @media (max-width: 600px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const Content = styled.div`
  padding: ${tokens.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[8]};
`;

const Section = styled.div``;

const SectionTitle = styled.h2`
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin: 0 0 ${tokens.spacing[4]} 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${tokens.spacing[4]};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  font-size: ${tokens.typography.fontSize.lg};
  color: ${tokens.colors.neutral[500]};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[6]};
  margin: ${tokens.spacing[6]};
  background: ${tokens.colors.semantic.error}15;
  color: ${tokens.colors.semantic.error};
  border-radius: ${tokens.borderRadius.md};
`;

const SettingsCard = styled.div`
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[6]};
`;

const SettingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${tokens.spacing[4]};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SettingsLabel = styled.div`
  flex: 1;
`;

const SettingsLabelText = styled.div`
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[1]};
`;

const SettingsDescription = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[2]};
`;

const PercentInput = styled.input`
  width: 100px;
  padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
  border: 2px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[800]};
  text-align: right;
  transition: border-color ${tokens.animation.duration.fast};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary[500]};
  }

  &:disabled {
    background: ${tokens.colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const PercentSymbol = styled.span`
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[600]};
`;

const SettingsActions = styled.div`
  display: flex;
  gap: ${tokens.spacing[3]};
  margin-top: ${tokens.spacing[2]};

  @media (max-width: 600px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const SettingsMessage = styled.div<{ $isError?: boolean }>`
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
  background: ${props => props.$isError
    ? `${tokens.colors.semantic.error}15`
    : `${tokens.colors.semantic.success}15`};
  color: ${props => props.$isError
    ? tokens.colors.semantic.error
    : tokens.colors.semantic.success};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
`;

const SettingsNote = styled.div`
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
  background: ${tokens.colors.neutral[100]};
  border-left: 3px solid ${tokens.colors.primary[500]};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};
  line-height: 1.5;
`;

const SectionSubtitle = styled.h3`
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin: 0 0 ${tokens.spacing[3]} 0;
`;

const PricingGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[3]};
`;

const PricingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${tokens.spacing[4]};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PriceInput = styled.input`
  width: 140px;
  padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
  border: 2px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[800]};
  text-align: right;
  transition: border-color ${tokens.animation.duration.fast};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary[500]};
  }

  &:disabled {
    background: ${tokens.colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${tokens.colors.neutral[200]};
  margin: ${tokens.spacing[4]} 0;
`;
